// import { transparentize } from 'polished'
import { useMemo } from 'react'
import BgBeach from '../../src/assets/svg/beach.svg'
import BgWater from '../../src/assets/svg/water-movement.svg'
import * as React from 'react'
import styled, {
  ThemeProvider as StyledComponentsThemeProvider,
  createGlobalStyle,
  css,
  DefaultTheme,
} from 'styled-components'
import { useIsDarkMode } from '../state/user/hooks'
import { Text, TextProps } from 'rebass'
import { Colors } from './styled'
import DiamondCircleLight from '../../src/assets/icons/diamond-circle-light.svg'
import DiamondCircleDark from '../../src/assets/icons/diamond-circle-dark.svg'
import DiamondBoxLight from '../../src/assets/icons/diamond-box-light.svg'
import DiamondBoxDark from '../../src/assets/icons/diamond-box-dark.svg'
import LockChartLight from '../../src/assets/icons/lock-chart-light.svg'
import LockChartDark from '../../src/assets/icons/lock-chart-dark.svg'
import DiamondBridgeLight from '../../src/assets/icons/diamond-bridge-light.svg'
import DiamondBridgeDark from '../../src/assets/icons/diamond-bridge-dark.svg'
import LaptopLight from '../../src/assets/icons/laptop-light.svg'
import LaptopDark from '../../src/assets/icons/laptop-dark.svg'
import UsersLinkLight from '../../src/assets/icons/users-link-light.svg'
import UsersLinkDark from '../../src/assets/icons/users-link-dark.svg'

export * from './components'

const MEDIA_WIDTHS = {
  upToExtraSmall: 0,
  upToSmall: 720,
  upToMedium: 960,
  upToLarge: 1280,
  upToXLarge: 1920
}

const mediaWidthTemplates: { [width in keyof typeof MEDIA_WIDTHS]: typeof css } = Object.keys(MEDIA_WIDTHS).reduce(
  (accumulator, size) => {
    ;(accumulator as any)[size] = (a: any, b: any, c: any) => css`
      @media (max-width: ${(MEDIA_WIDTHS as any)[size]}px) {
        ${css(a, b, c)}
      }
    `
    return accumulator
  },
  {}
) as any

const white = '#FFFFFF'
const black = '#000000'
const navy = '#184d76'
const dblue = '#13607a'
const boldblue = '#1b4f78'

export function colors(darkMode: boolean): Colors {
  return {
    // base
    white,
    black,
    navy,
    dblue,
    boldblue,

    // text
    text1: darkMode ? '#FFFFFF' : '#274d73', // Site Text
    text2: darkMode ? '#C3C5CB' : '#565A69', // Love Boat's default
    text3: darkMode ? '#6C7284' : '#888D9B',
    text4: darkMode ? '#565A69' : '#C3C5CB',
    text5: darkMode ? '#2C2F36' : '#EDEEF2',
    text6: darkMode ? '#FFFFFF' : '#37445d',

    // backgrounds / greys
    bg1: darkMode ? '#37445d' : '#FFFFFF',
    // bg2: darkMode ? '#2C2F36' : '#F7F8FA',
    bg2: darkMode ? '#565A69': '#e7e7e7',
    bg3: darkMode ? '#37445d' : '#FFFFFF',
    bg4: darkMode ? '#565A69' : '#CED0D9',
    bg5: darkMode ? '#6C7284' : '#888D9B',
    bg6: darkMode ? '#2e4162' : '#dae8f3',
    bg7: darkMode ? '#f7f7f7' : '#37445d',

    //specialty colors
    modalBG: darkMode ? 'rgba(0,0,0,.425)' : 'rgba(0,0,0,0.3)',
    advancedBG: darkMode ? 'rgba(0,0,0,0.1)' : 'rgba(255,255,255,0.6)',


    // Icons
    featureIcon1: darkMode ? DiamondCircleDark: DiamondCircleLight,
    featureIcon2: darkMode ? DiamondBoxDark: DiamondBoxLight,
    featureIcon3: darkMode ? LockChartDark: LockChartLight,
    featureIcon4: darkMode ? DiamondBridgeDark: DiamondBridgeLight,
    featureIcon5: darkMode ? LaptopDark: LaptopLight,
    featureIcon6: darkMode ? UsersLinkDark: UsersLinkLight,


    //primary colors
    //primary1: darkMode ? '#2172E5' : '#539FCE',
    primary1: darkMode ? '#274d73' : '#274d73',
    primary2: darkMode ? '#3680E7' : '#FF8CC3',
    primary3: darkMode ? '#4D8FEA' : '#FF99C9',
    primary4: darkMode ? '#376bad70' : '#539FCE',
    primary5: darkMode ? '#153d6f70' : '#D7E9F4',
    primary6: darkMode ? '#153d6f70' : '#D7E9F4',
    primary7: darkMode ? '#153d6f70' : '#FFFFFF',
    primary8: darkMode ? '#FFFFFF' : '#1b4f78',
    primary9: darkMode ? '#FFFFFF' : '#ebebeb',

    // color text
    primaryText1: darkMode ? '#6da8ff' : '#17223B',
    primaryText2: darkMode ? '#e78d81' : '#e78d81',
    primaryTextInfo: darkMode ? '#e7e7e7' : '#333333',
    featureTitleColor: darkMode ? '#f16ba2' : '#f16ba2',
    // secondary colors
    secondary1: darkMode ? '#00b38a' : '#17223B',
    secondary2: darkMode ? '#17000b26' : '#539FCE',
    secondary3: darkMode ? '#17000b26' : '#D7E9F4',

    // other
    // red1: '#FD4040',
    red1: '#FF000A', // LBDexGreen Red
    red2: '#F82D3A',
    red3: '#D60000',
    green1: '#27AE60',
    green2: '#00A968',
    green3: '#00B38A', // LBDex Green
    yellow1: '#FFE270',
    yellow2: '#F3841E',
    // blue1: '#2172E5',
    blue1: '#274d73',
    blue2: '#274d73', // LBDex Blue Text
    orange1: '#f7941d', // LBDexGreen Orange

    // dont wanna forget these blue yet
    // blue4: darkMode ? '#153d6f70' : '#C4D9F8',
    // blue5: darkMode ? '#153d6f70' : '#EBF4FF',
  }
}

export function theme(darkMode: boolean): DefaultTheme {
  return {
    ...colors(darkMode),

    grids: {
      sm: 8,
      md: 12,
      lg: 24,
    },

    //shadows
    shadow1: darkMode ? '#000' : '#2F80ED',

    // media queries
    mediaWidth: mediaWidthTemplates,

    // css snippets
    flexColumnNoWrap: css`
      display: flex;
      flex-flow: column nowrap;
    `,
    flexRowNoWrap: css`
      display: flex;
      flex-flow: row nowrap;
    `,
  }
}

export default function ThemeProvider({ children }: { children: React.ReactNode }) {
  const darkMode = useIsDarkMode()

  const themeObject = useMemo(() => theme(darkMode), [darkMode])

  return <StyledComponentsThemeProvider theme={themeObject}>{children}</StyledComponentsThemeProvider>
}

const TextWrapper = styled(Text)<{ color: keyof Colors }>`
  color: ${({ color, theme }) => (theme as any)[color]};
`

const StatTitleWrapper = styled(Text)<{ color: keyof Colors }>`
  color: ${({ color, theme }) => (theme as any)[color]};
  padding: 3px 0;
  text-align:left; 
`
const StatValueWrapper = styled(Text)<{ color: keyof Colors }>`
  color: ${({ color, theme }) => (theme as any)[color]};
  padding: 3px 0;
  text-align:right;

`

const TitleWrapper = styled(Text)<{ color: keyof Colors }>`
  color: ${({ color, theme }) => (theme as any)[color]};
  text-align:center;
  text-shadow:
    0 0 7px #fff,
    0 0 10px #fff,
    0 0 21px #fff,
    0 0 42px #0fe,
    0 0 82px #0fe,
    0 0 92px #0fe,
    0 0 102px #0fe,
    0 0 151px #0fe;
`
const HomeTitleWrapper = styled(Text)<{ color: keyof Colors }>`
  color: ${({ color, theme }) => (theme as any)[color]};
  text-align:center;
  text-shadow:
    0 0 7px #fff,
    0 0 10px #fff,
    0 0 21px #fff,
    0 0 42px #f5e0ae,
    0 0 82px #f5e0ae,
    0 0 92px #f5e0ae,
    0 0 102px #f5e0ae,
    0 0 151px #f5e0ae;
`

export const TYPE = {
  main(props: TextProps) {
    return <TextWrapper fontWeight={500} color={'text2'} {...props} />
  },
  link(props: TextProps) {
    return <TextWrapper fontWeight={500} color={'primary1'} {...props} />
  },
  black(props: TextProps) {
    return <TextWrapper fontWeight={500} color={'text1'} {...props} />
  },
  white(props: TextProps) {
    return <TextWrapper fontWeight={500} color={'white'} {...props} />
  },
  textmode(props: TextProps) {
    return <TextWrapper fontWeight={500} color={'primary8'} {...props} />
  },
  statTitle(props: TextProps) {
    return <StatTitleWrapper  color={'text1'} {...props} />
  },
  statValue(props: TextProps) {
    return <StatValueWrapper  color={'text1'} {...props} />
  },
  navy(props: TextProps) {
    return <TextWrapper fontWeight={500} color={'text6'} {...props} />
  },
  dblue(props: TextProps) {
    return <TextWrapper fontWeight={500} color={'dblue'} {...props} />
  },
  boldblue(props: TextProps) {
    return <TextWrapper fontWeight={500} color={'boldblue'} {...props} />
  },
  body(props: TextProps) {
    return <TextWrapper fontWeight={400} fontSize={16} color={'text1'} {...props} />
  },
  featureBody(props: TextProps) {
    return <TextWrapper fontWeight={400} fontSize={14} color={'text1'} {...props} />
  },
  featureTitle(props: TextProps) {
    return <TextWrapper fontWeight={600} fontSize={14} color={'featureTitleColor'} {...props} />
  },
  featureSectionTitle(props: TextProps) {
    return <HomeTitleWrapper fontSize={32} fontWeight={1000} color={'blue2'} {...props} />
  },
  largeHeader(props: TextProps) {
    return <TextWrapper fontWeight={600} fontSize={24} {...props} />
  },
  mediumHeader(props: TextProps) {
    return <TextWrapper fontWeight={500} fontSize={20} {...props} />
  },
  subHeader(props: TextProps) {
    return <TextWrapper fontWeight={400} fontSize={14} {...props} />
  },
  small(props: TextProps) {
    return <TextWrapper fontWeight={500} fontSize={11} {...props} />
  },
  blue(props: TextProps) {
    return <TextWrapper fontWeight={500} color={'blue1'} {...props} />
  },
  yellow(props: TextProps) {
    return <TextWrapper fontWeight={500} color={'yellow1'} {...props} />
  },
  darkGray(props: TextProps) {
    return <TextWrapper fontWeight={500} color={'text3'} {...props} />
  },
  gray(props: TextProps) {
    return <TextWrapper fontWeight={500} color={'bg3'} {...props} />
  },
  italic(props: TextProps) {
    return <TextWrapper fontWeight={500} fontSize={12} fontStyle={'italic'} color={'text2'} {...props} />
  },
  error({ error, ...props }: { error: boolean } & TextProps) {
    return <TextWrapper fontWeight={500} color={error ? 'red1' : 'text2'} {...props} />
  },
  pageTitle(props: TextProps) {
    return <TitleWrapper fontSize={36} fontWeight={1000} color={'blue2'} {...props} />
  },
  subTitle(props: TextProps) {
    return <TitleWrapper whiteSpace={'nowrap'} fontSize={28} fontWeight={1000} color={'blue2'} {...props} />
  },
  boxTitle(props: TextProps) {
    return <TextWrapper whiteSpace={'nowrap'} fontSize={20} fontWeight={1000} color={'text1'} {...props} />
  },
}

export const FixedGlobalStyle = createGlobalStyle`
html, input, textarea, button {
  font-family: 'Poppins', sans-serif;
  font-display: fallback;
}
@supports (font-variation-settings: normal) {
  html, input, textarea, button {
    font-family: 'Poppins', sans-serif;
  }
}

html,
body {
  margin: 0;
  padding: 0;
}

 a {
   color: ${colors(false).blue1}; 
 }

* {
  box-sizing: border-box;
}

button {
  user-select: none;
}

html {
  font-size: 16px;
  font-variant: none;
  -webkit-font-smoothing: antialiased;
  -moz-osx-font-smoothing: grayscale;
  -webkit-tap-highlight-color: rgba(0, 0, 0, 0);
  font-feature-settings: 'ss01' on, 'ss02' on, 'cv01' on, 'cv03' on;
  
}
`

export const ThemedGlobalStyle = createGlobalStyle`
html {
  color: ${({ theme }) => theme.text1};
  background-color: ${({ theme }) => theme.bg2};
}

body {
  background-image: url('${BgWater}') !important;
  min-height: 100vh;
  background-attachment: fixed;
  background-position: top;
  background-repeat: no-repeat;
  background-size: cover;
}
`

export const BeachGlobalStyle = createGlobalStyle`
html {
  color: ${({ theme }) => theme.text1};
  background-color: ${({ theme }) => theme.bg2};
}

body {
  background-image: url('${BgBeach}') !important;
  min-height: 100vh;
  background-attachment: fixed;
  background-position: top;
  background-repeat: no-repeat;
  background-size: cover;
  
}
`